// Column-major 4x4 matrices (WebGL layout). World space: x right, y UP, z toward the viewer (right-handed).
export type Mat4 = Float32Array;
export type Vec3 = [number, number, number];
export const rad = (deg: number): number => (deg * Math.PI) / 180;

export function identity(): Mat4 {
  const m = new Float32Array(16); m[0] = m[5] = m[10] = m[15] = 1; return m;
}
/** a * b (apply b first, then a). */
export function multiply(a: Mat4, b: Mat4): Mat4 {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) {
    let s = 0;
    for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
    o[c * 4 + r] = s;
  }
  return o;
}
export function translation(x: number, y: number, z: number): Mat4 {
  const m = identity(); m[12] = x; m[13] = y; m[14] = z; return m;
}
export function scaling(x: number, y: number, z: number): Mat4 {
  const m = identity(); m[0] = x; m[5] = y; m[10] = z; return m;
}
export function rotationX(a: number): Mat4 {
  const m = identity(), c = Math.cos(a), s = Math.sin(a);
  m[5] = c; m[6] = s; m[9] = -s; m[10] = c; return m;
}
export function rotationY(a: number): Mat4 {
  const m = identity(), c = Math.cos(a), s = Math.sin(a);
  m[0] = c; m[2] = -s; m[8] = s; m[10] = c; return m;
}
export function rotationZ(a: number): Mat4 {
  const m = identity(), c = Math.cos(a), s = Math.sin(a);
  m[0] = c; m[1] = s; m[4] = -s; m[5] = c; return m;
}
export function perspective(fovyRad: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovyRad / 2), m = new Float32Array(16);
  m[0] = f / aspect; m[5] = f; m[10] = (far + near) / (near - far); m[11] = -1; m[14] = (2 * far * near) / (near - far);
  return m;
}
export function lookAt(eye: Vec3, target: Vec3, up: Vec3): Mat4 {
  let zx = eye[0] - target[0], zy = eye[1] - target[1], zz = eye[2] - target[2];
  const zl = Math.hypot(zx, zy, zz) || 1; zx /= zl; zy /= zl; zz /= zl;
  let xx = up[1] * zz - up[2] * zy, xy = up[2] * zx - up[0] * zz, xz = up[0] * zy - up[1] * zx;
  const xl = Math.hypot(xx, xy, xz) || 1; xx /= xl; xy /= xl; xz /= xl;
  const yx = zy * xz - zz * xy, yy = zz * xx - zx * xz, yz = zx * xy - zy * xx;
  const m = new Float32Array(16);
  m[0] = xx; m[1] = yx; m[2] = zx; m[4] = xy; m[5] = yy; m[6] = zy; m[8] = xz; m[9] = yz; m[10] = zz;
  m[12] = -(xx * eye[0] + xy * eye[1] + xz * eye[2]);
  m[13] = -(yx * eye[0] + yy * eye[1] + yz * eye[2]);
  m[14] = -(zx * eye[0] + zy * eye[1] + zz * eye[2]);
  m[15] = 1;
  return m;
}
export function transformPoint(m: Mat4, p: Vec3): Vec3 {
  const [x, y, z] = p;
  const w = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!;
  return [(m[0]! * x + m[4]! * y + m[8]! * z + m[12]!) / w, (m[1]! * x + m[5]! * y + m[9]! * z + m[13]!) / w,
          (m[2]! * x + m[6]! * y + m[10]! * z + m[14]!) / w];
}

/** Model matrix. Order: T * Ry(yaw) * Rx(pitch) * Rz(roll) * S.
 *  Conventions (same as the schema docs): rotation = clockwise on screen, positive rotation_y moves the
 *  right edge away from the viewer, positive rotation_x moves the top edge away from the viewer. */
export function modelMatrix(px: number, py: number, pz: number, rotX: number, rotY: number, rotZ: number,
                            sx: number, sy: number, sz: number): Mat4 {
  let m = translation(px, py, pz);
  m = multiply(m, rotationY(rad(rotY)));
  m = multiply(m, rotationX(rad(-rotX)));
  m = multiply(m, rotationZ(rad(-rotZ)));
  return multiply(m, scaling(sx, sy, sz));
}

export interface ViewSetup { view: Mat4; proj: Mat4; eye: Vec3; distance: number }
/** Default framing: the z=0 plane maps 1:1 to canvas pixels when distance=1 (for any fov).
 *  The camera orbits the (panned) scene center; roll rotates the image clockwise. */
export function cameraMatrices(width: number, height: number, cam: { fov: number; distance: number; x: number;
  y: number; z: number; roll: number; pitch: number; yaw: number }): ViewSetup {
  const fov = rad(cam.fov);
  const dist = (height / 2 / Math.tan(fov / 2)) * cam.distance;
  const yaw = rad(cam.yaw), pitch = rad(cam.pitch);
  const target: Vec3 = [cam.x, -cam.y, cam.z];
  const eye: Vec3 = [target[0] - dist * Math.sin(yaw) * Math.cos(pitch), target[1] - dist * Math.sin(pitch),
                     target[2] + dist * Math.cos(yaw) * Math.cos(pitch)];
  const view = multiply(rotationZ(-rad(cam.roll)), lookAt(eye, target, [0, 1, 0]));
  const near = Math.max(1, dist * 0.02), far = dist + 60000;
  return { view, proj: perspective(fov, width / height, near, far), eye, distance: dist };
}
